import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import { siteMetadata } from "../content/siteMetadata";

/**
 * Global application context for the marketing site.
 * Translation: Global application context for the marketing site.
 */
export const ApplicationContext = createContext();

/**
 * Initial reducer state for the marketing experience.
 * Translation: Initial reducer state for the marketing experience.
 */
const initialState = {
  theme: "dark",
  isMobileMenuOpen: false,
  isModalOpen: false,
  bodyOverflow: "auto",
  activeSection: null,
  prefersReducedMotion: false,
  pageMetadata: {
    title: siteMetadata.defaultTitle,
    description: siteMetadata.defaultDescription,
    image: siteMetadata.socialImage,
    pathname: "/",
  },
  pwaMode: "manifest-only",
};

/**
 * Reducer for all site-level interactions.
 * Translation: Reducer for all site-level interactions.
 *
 * @param {Object} state - Current state snapshot.
 * @param {Object} action - Dispatched action.
 * @returns {Object} Updated reducer state.
 */
const reducer = (state, action) => {
  if (!action?.type) {
    return state;
  }

  switch (action.type) {
    case "SET_MOBILE_MENU_OPEN":
      return {
        ...state,
        isMobileMenuOpen: action.payload,
      };

    case "IS_MODAL_OPEN":
      return {
        ...state,
        isModalOpen: action.payload,
      };

    case "SET_BODY_OVERFLOW":
      return {
        ...state,
        bodyOverflow: action.payload,
      };

    case "SET_ACTIVE_SECTION":
      return {
        ...state,
        activeSection: action.payload,
      };

    case "SET_PREFERS_REDUCED_MOTION":
      return {
        ...state,
        prefersReducedMotion: action.payload,
      };

    case "SET_PAGE_METADATA":
      return {
        ...state,
        pageMetadata: {
          ...state.pageMetadata,
          ...action.payload,
        },
      };

    case "SET_PWA_MODE":
      return {
        ...state,
        pwaMode: action.payload,
      };

    default:
      return state;
  }
};

/**
 * Provide reducer state and thunk-capable dispatch to the app tree.
 * Translation: Provide reducer state and thunk-capable dispatch to the app tree.
 *
 * @param {{children: React.ReactNode}} props - Provider props.
 * @returns {JSX.Element} Provider wrapper.
 */
export const ApplicationContextProvider = ({ children }) => {
  const [state, defaultDispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /**
   * Dispatch plain actions or lightweight thunks.
   * Translation: Dispatch plain actions or lightweight thunks.
   *
   * @param {Function|Object} action - Thunk or reducer action.
   * @returns {void}
   */
  const dispatch = useCallback(
    (action) => {
      if (!action) {
        return;
      }

      if (typeof action === "function") {
        action(dispatch, stateRef.current);
        return;
      }

      defaultDispatch(action);
    },
    [defaultDispatch]
  );

  const contextValue = useMemo(() => [state, dispatch], [state, dispatch]);

  return (
    <ApplicationContext.Provider value={contextValue}>
      {children}
    </ApplicationContext.Provider>
  );
};
